// Orchestrateur de session backend RMF cote frontend. Source de verite unique pour :
// - le cycle de vie (idle/starting/waiting/ready/error/lost)
// - le polling /health apres /start 202
// - le heartbeat 15 min vers le control_plane EC2 (cf ADR 018)
// - la banniere "unreachable" quand le heartbeat echoue plusieurs fois consecutivement
//
// Le store est singleton global : sa duree de vie depasse celle de /welcome pour que la
// session backend reste maintenue vivante meme quand l'utilisateur navigue vers /[map].
// Le cleanup explicite passe par sessionStore.stop() appele depuis fullLogout (ADR 018).

import { getMap } from '$lib/rmf/maps';
import { tasksStore } from '$lib/rmf/tasks.svelte';
import { backendClient, type BackendPhase, type QuotaInfo, type SessionInfo } from './backend-client';
import {
  QuotaExceededError,
  SessionFailedError,
  SessionMissingError,
  SessionNotFoundError,
  SessionNotReadyError
} from './errors';
import { waitUntilRmfReady } from './readiness-polling';

/** Intervalle entre deux ticks heartbeat. Aligne sur le refresh access_token Cognito (15 min). */
const HEARTBEAT_INTERVAL_MS = 15 * 60 * 1000;
/** Marge de securite avant expiration du JWT trusted rmf-web : on rafraichit au plus tard
 *  2 min avant pour absorber le RTT control_plane (~100 ms) + jitter timer JS. TTL backend
 *  ~15 min, donc effectif d'usage ~13 min entre deux fetch. */
const TRUSTED_TOKEN_MARGIN_MS = 2 * 60 * 1000;
/** Delai entre les retries rapides intra-tick. 3 tentatives totales dans la foulee (~10-15 s). */
const HEARTBEAT_RETRY_DELAY_MS = 5_000;
/** Nombre de retries rapides apres un fail initial (0 = pas de retry, 2 = 3 tentatives totales). */
const HEARTBEAT_MAX_RETRIES = 2;
/** Intervalle entre deux polls /status. La Lambda repond en ~50-150 ms, 2 s est confortable
 *  cote couts (Lambda invocations) sans dégrader la perception (boot total ~30-60 s). */
const POLL_INTERVAL_MS = 2_000;
/** Timeout total du polling /status. Au-dela on considere la session morte cote front. */
const POLL_TIMEOUT_MS = 300_000;
/** Fallback expectedFleets si on n'arrive pas a determiner le world courant via /world-status.
 *  1 = le minimum theorique (office, battle_royale), peu severe : on attend qu'au moins une
 *  fleet soit register avant de se declarer ready. */
const FALLBACK_EXPECTED_FLEETS = 1;
/** Nombre de retries automatiques sur timeout phase 4 (fleets/building_map). Cf bug task #36.
 *  Surchargeable via VITE_RMF_READY_MAX_RETRIES (string -> int) pour les tests. */
const RMF_READY_MAX_RETRIES = Number(import.meta.env.VITE_RMF_READY_MAX_RETRIES ?? '1');

/** Cle sessionStorage pour persister le startedAt timestamp. Le but : un F5 pendant le
 *  wake-up backend ne doit pas remettre le compteur "elapsed" a 0, puisque la session backend
 *  elle-meme est preservee cote DDB. Scoped tab (sessionStorage) volontaire : si l'user ouvre
 *  une nouvelle tab, on demarre un compteur frais (cohrent avec le sub Cognito qui peut etre
 *  different et avec le pattern token-storage.ts). */
const STARTED_AT_KEY = 'rmf:session:startedAt';
/** Age max au-dela duquel on considere un startedAt persiste comme stale (la session backend a
 *  ete swept depuis longtemps). Au-dela, on demarre un nouveau cycle. */
const STARTED_AT_MAX_AGE_MS = 60 * 60 * 1000;

function loadPersistedStartedAt(): number | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(STARTED_AT_KEY);
  if (!raw) return null;
  const t = Number(raw);
  if (!Number.isFinite(t)) return null;
  if (Date.now() - t > STARTED_AT_MAX_AGE_MS) return null;
  return t;
}

function persistStartedAt(t: number): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STARTED_AT_KEY, String(t));
  } catch {
    // sessionStorage quota / privacy mode : tolere silencieux, on perd la persistance F5.
  }
}

function clearPersistedStartedAt(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(STARTED_AT_KEY);
}

export type HeartbeatStatus = 'ok' | 'unreachable';

function createSessionStore() {
  let phase = $state<BackendPhase | 'lost'>('idle');
  let sessionInfo = $state<SessionInfo | null>(null);
  let error = $state<unknown>(null);
  let elapsedSeconds = $state(0);
  let heartbeatStatus = $state<HeartbeatStatus>('ok');
  // Quota cumule 24h glissantes. Refetch au /start et a chaque heartbeat reussi (15 min).
  // Pas de tick local : entre deux refetch, le HUD affiche la derniere valeur connue.
  let quota = $state<QuotaInfo | null>(null);
  let quotaUpdatedAt = $state<Date | null>(null);
  // Degraded = phase 4 a timeout meme apres retry (fleets register incomplet, bug task #36).
  // Le store passe quand meme phase='ready' pour debloquer le user, mais la UI peut afficher
  // une banniere d'avertissement et proposer un retry manuel via restart() ou switchWorld().
  let degraded = $state(false);
  // Nombre de fleets actuellement publiees par rmf-web. Mis a jour en continu pendant la
  // phase 4 ; permet a la UI de montrer la progression (X/Y fleets).
  let fleetsCount = $state(0);

  // JWT trusted rmf-web (cf ADR 032). Pas exposé via getter : seul getValidTrustedToken() est
  // public, ce qui force les consommateurs (Phase 3 Socket.IO, REST `/api/*`) a passer par le
  // single-flight et la verification d'expiration. Non-reactif : aucun composant n'a besoin
  // d'observer le token, et un $state pourrait inviter un usage inapproprie via tracking.
  let trustedToken: string | null = null;
  let trustedExpiresAt = 0;
  let trustedRefreshInFlight: Promise<string> | null = null;

  // Timers internes - pas exposes au consumer.
  let elapsedTimer: ReturnType<typeof setInterval> | undefined;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  let heartbeatTimer: ReturnType<typeof setInterval> | undefined;
  // Drapeau pour interrompre proprement les boucles async pending (polling, retries).
  let cancelled = false;
  // Timestamp du debut effectif du cycle de wake-up. Persiste en sessionStorage pour survivre
  // a un F5 (le compteur "Elapsed" continue d'incrementer plutot que de reset a 0). Reset
  // quand backend.start() renvoie wasExisting=false (= nouvelle session DDB).
  let startedAt: number | null = null;

  function clearTimers() {
    if (elapsedTimer) {
      clearInterval(elapsedTimer);
      elapsedTimer = undefined;
    }
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = undefined;
    }
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = undefined;
    }
  }

  function resetState() {
    phase = 'idle';
    sessionInfo = null;
    error = null;
    elapsedSeconds = 0;
    heartbeatStatus = 'ok';
    quota = null;
    quotaUpdatedAt = null;
    degraded = false;
    fleetsCount = 0;
    startedAt = null;
    clearPersistedStartedAt();
    // Purge du JWT trusted : le sub Cognito et le DNS de session changent au prochain start,
    // l'ancien token serait au mieux inutilisable, au pire risque cross-session. La promise
    // in-flight (si refresh en cours pendant un stop) resoudra dans le vide ; son .then()
    // re-affectera trustedToken mais aucun consommateur ne le lit en phase=idle.
    trustedToken = null;
    trustedExpiresAt = 0;
    // Purge le building_map cache cote tasksStore : le prochain start (potentiellement sur un
    // autre world apres logout) doit refetch via la readiness chain. Sans ce clear, on
    // afficherait les waypoints stale du world precedent dans le DispatchForm.
    tasksStore.clearBuildingMap();
  }

  /**
   * Refetch le quota. Best-effort : un echec garde la valeur precedente (le HUD reste
   * affiche, juste un peu obsolete). On ne propage pas l'erreur pour eviter de polluer
   * la phase d'erreur de la session (le quota n'est pas critique pour le flow).
   */
  async function refreshQuota(): Promise<void> {
    try {
      quota = await backendClient.getQuota();
      quotaUpdatedAt = new Date();
    } catch (err) {
      console.warn('Quota refresh failed (non-fatal):', err);
    }
  }

  async function pollUntilReady() {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (!cancelled) {
      if (Date.now() >= deadline) {
        throw new SessionNotReadyError();
      }
      // Polling via la Lambda /status (depuis v0.1.22). La Lambda voit l'etat EC2 + sonde
      // HTTPS publique https://<dns>/health depuis AWS, avec validation du cert, donc pas de
      // souci NXDOMAIN local browser ni de TLS reset cold start. Un cert de session invalide
      // reste lu comme booting jusqu'au timeout. Une seule requete par tour vers api.rmf.tf
      // (toujours up).
      let status;
      try {
        status = await backendClient.getStatus();
      } catch (err) {
        // 401 = refresh deja tente par getValidAccessToken (single-flight). 5xx/network
        // transient -> retry court (2 s) sans casser le polling.
        console.warn('Status poll failed (retry):', err);
        await new Promise<void>((resolve) => {
          pollTimer = setTimeout(resolve, POLL_INTERVAL_MS);
        });
        continue;
      }
      if (status.state === 'ready') {
        // Phase 2 atteinte (control_plane up). On NE passe PAS encore en phase='ready' :
        // c'est runReadinessChain qui le fera apres phases 3 et 4. La transition vers
        // 'rmf_initializing' debloque l'UI pour afficher un message distinct du booting EC2.
        if (!cancelled) phase = 'rmf_initializing';
        return;
      }
      if (status.state === 'failed') {
        throw new SessionFailedError(status.reason);
      }
      if (status.state === 'no_session') {
        throw new SessionMissingError();
      }
      // booting -> next poll dans POLL_INTERVAL_MS
      await new Promise<void>((resolve) => {
        pollTimer = setTimeout(resolve, POLL_INTERVAL_MS);
      });
    }
  }

  /**
   * Determine le nombre de fleets attendu en interrogeant /world-status pour connaitre le
   * world courant cote backend, puis en lisant MapDescriptor.expectedFleets. Fallback prudent
   * si /world-status echoue (cas inattendu apres /status=ready, mais on garde la chaine en
   * route plutot que d'echouer le boot).
   */
  async function resolveExpectedFleets(): Promise<number> {
    try {
      const ws = await backendClient.getWorldStatus();
      const map = getMap(ws.currentWorld);
      if (map && map.expectedFleets > 0) return map.expectedFleets;
    } catch (err) {
      console.warn('resolveExpectedFleets failed, using fallback:', err);
    }
    return FALLBACK_EXPECTED_FLEETS;
  }

  /**
   * Enchaine les phases 3 et 4 avec retry auto sur timeout phase 4.
   *
   * Sur timeout fleets (bug task #36 upstream Open-RMF, fleet_adapter ne se register pas
   * tous), tente jusqu'a RMF_READY_MAX_RETRIES de POST /switch-world avec le world courant.
   * Le control_plane interprete ca comme un restart de rmf_core (systemctl restart) qui force
   * tous les fleet_adapter a recharger. Si tous les retries echouent, on passe en phase=ready
   * avec degraded=true : l'UI doit montrer une banniere + bouton "Relancer le chargement".
   */
  async function runReadinessChain(): Promise<void> {
    const expectedFleets = await resolveExpectedFleets();
    for (let attempt = 0; attempt <= RMF_READY_MAX_RETRIES; attempt += 1) {
      if (cancelled) return;
      // A chaque tentative, on revient en rmf_initializing : le restart RMF refait toute la
      // chaine (phase=ready intermediaire puis fully_ready).
      phase = 'rmf_initializing';
      let result;
      try {
        result = await waitUntilRmfReady({
          expectedFleets,
          cancelled: () => cancelled,
          onPhase: (progress) => {
            if (cancelled) return;
            if (progress.phase === 'rmf_loading') phase = 'rmf_loading';
            if (progress.fleetsCount !== undefined) fleetsCount = progress.fleetsCount;
          }
        });
      } catch (err) {
        // Throw phase 3 (timeout fully_ready ou phase=failed) : non-retryable cote front,
        // le control_plane est cassee plus profondement qu'un simple restart RMF ne resout.
        throw err;
      }
      if (cancelled) return;
      // Push le building_map fetched par la readiness chain vers tasksStore. Evite que
      // ensureBuildingMap fasse un 2e fetch quand l'user ouvre DispatchForm (qui pourrait
      // tomber sur un 404 si rmf-web hiccup juste apres). No-op si tasksStore en a deja un.
      if (result.buildingMap) tasksStore.setBuildingMap(result.buildingMap);
      if (!result.degraded) {
        phase = 'ready';
        return;
      }
      // Timeout phase 4 : on tente un restart RMF via switch-world idempotent.
      if (attempt < RMF_READY_MAX_RETRIES) {
        console.warn(
          `Phase 4 timeout (fleets=${result.fleetsCount}/${expectedFleets}), retry ${attempt + 1}/${RMF_READY_MAX_RETRIES} via switchWorld`
        );
        try {
          const ws = await backendClient.getWorldStatus();
          await backendClient.switchWorld(ws.currentWorld);
        } catch (err) {
          // Le retry switch-world a lui-meme echoue : on arrete la boucle, on passe degraded.
          console.warn('Retry switchWorld failed, accepting degraded state:', err);
          break;
        }
      }
    }
    // Tous les retries epuises : on debloque le user en degraded.
    degraded = true;
    if (!cancelled) phase = 'ready';
  }

  async function tickHeartbeat() {
    // Retries rapides intra-tick : 3 tentatives totales (~10-15 s avec 2x 5 s d'attente).
    // Couvre les glitchs reseau transitoires sans cri-er au loup, et avertit assez tot pour
    // que l'utilisateur sache que le sweep DDB (30 min) va bientot couper si ca persiste.
    for (let attempt = 0; attempt <= HEARTBEAT_MAX_RETRIES; attempt += 1) {
      try {
        await backendClient.heartbeat();
        heartbeatStatus = 'ok';
        // Le heartbeat est le bon moment pour refresh le quota : meme cadence (15 min) que
        // l'horloge serveur pour le calcul cumul. await (pas void) pour que la detection
        // quota=0 ci-dessous soit dans le meme tick. ~50-100ms d'overhead, negligeable.
        await refreshQuota();
        // Detection proactive quota epuise : si on tombe a 0 entre deux heartbeats, le backend
        // va couper l'EC2 dans la foulee. Plutot que d'attendre les erreurs Socket.IO et le
        // 404 du prochain heartbeat (15 min plus tard), on transitionne immediatement vers
        // 'lost' avec une error typee pour que l'UI affiche un message clair. Sans ca, l'user
        // voit juste un spam de connect_error sans contexte.
        if (quota?.remainingSeconds === 0 && phase === 'ready') {
          console.warn('Quota exhausted detected post-heartbeat, terminating session');
          error = new QuotaExceededError({
            usedSeconds: quota.usedSeconds,
            limitSeconds: quota.limitSeconds
          });
          stopHeartbeatLoop();
          phase = 'lost';
        }
        return;
      } catch (err) {
        // 404 SessionNotFoundError = la session a ete terminee cote backend (sweep, /stop
        // concurrent, autre tab). On stop le heartbeat loop et on signale au consommateur
        // via phase='lost' pour qu'il propose un restart.
        if (err instanceof SessionNotFoundError) {
          stopHeartbeatLoop();
          phase = 'lost';
          return;
        }
        // 403 QuotaExceededError sur le heartbeat lui-meme : meme issue (session morte cote
        // backend pour cause quota). On set l'error pour que l'UI affiche le message quota
        // dedie au lieu du generique "session terminee".
        if (err instanceof QuotaExceededError) {
          stopHeartbeatLoop();
          error = err;
          phase = 'lost';
          return;
        }
        // 5xx / network / timeout : retry rapide si on n'a pas epuise les tentatives.
        if (attempt < HEARTBEAT_MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, HEARTBEAT_RETRY_DELAY_MS));
          continue;
        }
        // Apres 3 echecs consecutifs : on affiche la banniere. Le prochain tick (15 min plus
        // tard) retentera depuis 0 ; un succes restaurera heartbeatStatus='ok'.
        heartbeatStatus = 'unreachable';
        console.warn('Heartbeat unreachable after retries', err);
      }
    }
  }

  function startHeartbeatLoop() {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    // Tick immediat des qu'on entre en phase=ready. setInterval n'arme que le PROCHAIN tick
    // a T+15min, donc sans ce premier tick un F5 a T+14min59s du load precedent laisserait
    // jusqu'a 29min59s sans heartbeat envoye (le timer JS est detruit avec le process au
    // reload), alors que le control_plane shutdown a 16min d'inactivite (cf ADR 018). En
    // bonus, refreshQuota() est appele dans tickHeartbeat -> le HUD quota est rafraichi
    // immediatement a l'arrivee sur /welcome au lieu d'attendre le 1er cycle de 15min.
    void tickHeartbeat();
    heartbeatTimer = setInterval(() => {
      void tickHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);
  }

  function stopHeartbeatLoop() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = undefined;
    }
  }

  async function start(): Promise<void> {
    // Idempotent : si une session est deja en cours ou prete, on ne relance pas. Le caller
    // peut force-restart via restart() qui reset puis appelle start().
    if (phase === 'starting' || phase === 'waiting' || phase === 'ready') return;

    cancelled = false;
    error = null;
    heartbeatStatus = 'ok';
    degraded = false;
    fleetsCount = 0;

    // startedAt : on prefere le timestamp persiste (F5 mid-wake-up) plutot que de remettre
    // le compteur a 0. Si pas de persistance ou stale (>1h), on demarre un nouveau cycle.
    // Si la session backend retourne wasExisting=false (cf plus bas), on l'overwrite.
    const persisted = loadPersistedStartedAt();
    startedAt = persisted ?? Date.now();
    if (persisted === null) persistStartedAt(startedAt);
    // Tick "from-timestamp" : on recalcule a chaque tick depuis startedAt au lieu d'incrementer
    // a +1. Avantages : robuste au tab inactif (setInterval throttle en background ; le compteur
    // resterait en retard), et au F5 (le 1er tick post-mount affiche directement la valeur
    // correcte sans transition par 0).
    elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
    elapsedTimer = setInterval(() => {
      if (startedAt !== null) elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
    }, 1000);

    try {
      phase = 'starting';
      sessionInfo = await backendClient.start();
      if (cancelled) return;
      // Si le backend a cree une NOUVELLE session (wasExisting=false), on remet le compteur
      // a 0 : un F5 sur un /welcome stale avait persiste un startedAt obsolete d'une session
      // precedente. wasExisting=true signifie au contraire que la DDB connait deja ce sub, donc
      // le startedAt persiste reflete le bon cycle en cours.
      if (sessionInfo.wasExisting === false) {
        startedAt = Date.now();
        persistStartedAt(startedAt);
        elapsedSeconds = 0;
      }
      // Pas de court-circuit sur wasExisting : le 200 indique seulement qu'une entree DDB
      // existe deja pour ce sub, pas que l'EC2 est reellement up. Cas concret : F5 sur /welcome
      // pendant le boot -> /start renvoie 200 (DDB ok depuis le 1er /start) mais l'instance est
      // encore en cours de demarrage. Seul /status (sonde HTTPS publique https://<dns>/health
      // depuis AWS par la Lambda, avec validation du cert) reflete l'etat reel. Cout : un GET
      // /status immediat (~100 ms) dans le cas ideal "deja prete", negligeable par rapport au
      // risque de passer en phase=ready avant que le backend soit reellement servable.
      phase = 'waiting';
      await pollUntilReady();
      // pollUntilReady passe phase='rmf_initializing' a la fin (phase 2 OK). On enchaine
      // phases 3 et 4 (health/full + fleets/building_map) avec retry auto sur timeout. C'est
      // runReadinessChain qui passe phase='ready' (eventuellement avec degraded=true). Le 1er
      // tick heartbeat (immediat au startHeartbeatLoop) declenche aussi le refreshQuota initial.
      if (cancelled) return;
      await runReadinessChain();
      if (!cancelled) startHeartbeatLoop();
    } catch (err) {
      console.error('Session start failed', err);
      if (!cancelled) {
        error = err;
        phase = 'error';
      }
    } finally {
      if (elapsedTimer) clearInterval(elapsedTimer);
      elapsedTimer = undefined;
    }
  }

  /**
   * Retourne un JWT trusted rmf-web valide (>2 min restants) pour authentifier Socket.IO et les
   * REST `/api/*` upstream. Refresh transparent si proche de l'expiration. Mirror du pattern
   * auth.getValidAccessToken cote Cognito.
   *
   * Single-flight : N appelants concurrent (ex: Socket.IO connect + un fetch REST /api/tasks
   * simultane) partagent la meme Promise. Sans cela, le control_plane verrait plusieurs
   * issue_rmf_web_token concurrents pour le meme sub — pas catastrophique (proxy stateless) mais
   * inutile.
   *
   * Throw SessionNotReadyError si appele avant phase=ready : sans DNS de session valide, on ne
   * sait pas ou taper le control_plane. Les consommateurs (Phase 3 Socket.IO) doivent gater
   * leur connect derriere phase=ready ou attendre via un $effect.
   */
  async function getValidTrustedToken(): Promise<string> {
    if (phase !== 'ready') {
      throw new SessionNotReadyError('Trusted token requires sessionStore.phase=ready');
    }
    if (trustedToken && trustedExpiresAt - Date.now() > TRUSTED_TOKEN_MARGIN_MS) {
      return trustedToken;
    }
    if (!trustedRefreshInFlight) {
      trustedRefreshInFlight = backendClient
        .getRmfWebToken()
        .then(({ token, expiresIn }) => {
          trustedToken = token;
          trustedExpiresAt = Date.now() + expiresIn * 1000;
          return token;
        })
        .catch((err) => {
          // Echec : on purge l'etat local pour forcer un re-fetch au prochain appel. Si la
          // session est morte cote serveur (401/404), le caller Socket.IO devra propager
          // l'erreur a sessionStore (transition phase='lost') via le handler connect_error.
          trustedToken = null;
          trustedExpiresAt = 0;
          throw err;
        })
        .finally(() => {
          trustedRefreshInFlight = null;
        });
    }
    return trustedRefreshInFlight;
  }

  /** Reset complet local + appel /stop backend. A appeler depuis fullLogout uniquement. */
  async function stop(): Promise<void> {
    cancelled = true;
    clearTimers();

    try {
      if (sessionInfo) await backendClient.stop();
    } catch (err) {
      console.warn('Backend stop during session.stop (non-fatal):', err);
    }

    resetState();
  }

  /** Reset local sans /stop backend. Pour le bouton "Reessayer" sur erreur. */
  async function restart(): Promise<void> {
    cancelled = true;
    clearTimers();
    resetState();
    await start();
  }

  /**
   * Relance uniquement la chaine de readiness RMF (phases 3+4) sans toucher au cold start
   * EC2. A appeler depuis le bouton "Relancer le chargement RMF" affiche quand degraded=true.
   * Differe de restart() qui detruit toute la session : ici on garde le control_plane up et
   * on retente uniquement le restart rmf_core via switchWorld idempotent (inclus dans
   * runReadinessChain).
   */
  async function retryRmfReady(): Promise<void> {
    if (phase === 'idle' || phase === 'starting' || phase === 'waiting') return;
    degraded = false;
    fleetsCount = 0;
    await runReadinessChain();
  }

  return {
    get phase() {
      return phase;
    },
    get sessionInfo() {
      return sessionInfo;
    },
    get error() {
      return error;
    },
    get elapsedSeconds() {
      return elapsedSeconds;
    },
    get heartbeatStatus() {
      return heartbeatStatus;
    },
    get quota() {
      return quota;
    },
    get quotaUpdatedAt() {
      return quotaUpdatedAt;
    },
    get degraded() {
      return degraded;
    },
    get fleetsCount() {
      return fleetsCount;
    },
    start,
    stop,
    restart,
    retryRmfReady,
    getValidTrustedToken
  };
}

export const sessionStore = createSessionStore();
